import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as React from "react";
import { ReactNode, isValidElement } from "react";
import { cn } from "../../lib/utils";
import { CONTROL_SIZE_TOKENS } from "./control-size";
import { Spinner } from "./spinner";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

const buttonVariants = cva(
  `inline-flex 
  items-center 
  justify-center 
  rounded-sm 
  text-fg 
   font-medium 
  transition-colors 
  focus-visible:outline-none 
  focus-visible:ring-2 
  focus-visible:ring-ring 
  focus-visible:ring-offset-2 
  whitespace-nowrap 
  flex-shrink-0

  disabled:opacity-60
  disabled:pointer-events-none 
  disabled:cursor-not-allowed 
  

  ring-offset-background
  text-fg`,

  {
    variants: {
      size: {
        // "default" is an alias of "md", kept only so old call sites
        // (`size="default"`) keep compiling.
        default: `${CONTROL_SIZE_TOKENS.md.height} ${CONTROL_SIZE_TOKENS.md.text} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.gap} ${CONTROL_SIZE_TOKENS.md.icon} ${CONTROL_SIZE_TOKENS.md.radius}`,
        xs: `${CONTROL_SIZE_TOKENS.xs.height} ${CONTROL_SIZE_TOKENS.xs.text} ${CONTROL_SIZE_TOKENS.xs.px} ${CONTROL_SIZE_TOKENS.xs.gap} ${CONTROL_SIZE_TOKENS.xs.icon} ${CONTROL_SIZE_TOKENS.xs.radius}`,
        sm: `${CONTROL_SIZE_TOKENS.sm.height} ${CONTROL_SIZE_TOKENS.sm.text} ${CONTROL_SIZE_TOKENS.sm.px} ${CONTROL_SIZE_TOKENS.sm.gap} ${CONTROL_SIZE_TOKENS.sm.icon} ${CONTROL_SIZE_TOKENS.sm.radius}`,
        md: `${CONTROL_SIZE_TOKENS.md.height} ${CONTROL_SIZE_TOKENS.md.text} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.gap} ${CONTROL_SIZE_TOKENS.md.icon} ${CONTROL_SIZE_TOKENS.md.radius}`,
        lg: `${CONTROL_SIZE_TOKENS.lg.height} ${CONTROL_SIZE_TOKENS.lg.text} ${CONTROL_SIZE_TOKENS.lg.px} ${CONTROL_SIZE_TOKENS.lg.gap} ${CONTROL_SIZE_TOKENS.lg.icon} ${CONTROL_SIZE_TOKENS.lg.radius}`,
        xl: `${CONTROL_SIZE_TOKENS.xl.height} ${CONTROL_SIZE_TOKENS.xl.text} ${CONTROL_SIZE_TOKENS.xl.px} ${CONTROL_SIZE_TOKENS.xl.gap} ${CONTROL_SIZE_TOKENS.xl.icon} ${CONTROL_SIZE_TOKENS.xl.radius}`,
        // "custom": no size classes at all — the caller fully controls sizing
        // via `className` (used for icon-only buttons with a fixed square size).
        custom: "",
      },
      variant: {
        default:
          "text-ac+1 bg-ac-4  border border-ac hover:border-ac+2 hover:bg-ac-3 ",
        strong: "text-ac+2 bg-ac border border-ac hover:bg-ac-2 ",
        destructive: `!bg-de+1 
                      text-white 
                      border border-de-1 
                      hover:bg-de/50

                      disabled:!bg-bg+1
                      disabled:text-fg-muted
                      disabled:border-gr-1 
                      
                      `,
        outline: "border border-bg+2 text-fg hover:border-bg+3",
        outlineDestructive: `!bg-red-100 
                      text-de+1 
                      border border-de-1 
                      hover:bg-de/50

                      disabled:!bg-bg+1
                      disabled:text-fg-muted
                      disabled:border-gr-1 
                      
                      `,

        // the input like variants are used for the combobox as it uses a button as triggger
        inputLike: `px-2 
          bg-white 
          dark:bg-bg 
          border 
          border-ring 
          font-normal 
          focus:outline-none 
          focus:border-ring-focus 
          disabled:cursor-not-allowed 
          disabled:bg-bg+1
          
          `,
        inputLikeDestructive:
          "border border-de+1 bg-de-3 placeholder:text-de+1",

        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        selected: "bg-ac-4 text-ac+1 font-normal",
        white:
          "bg-secondary-to-white border border-bg+3 text-foreground hover:bg-secondary/80 text-fg",
        ghost:
          "hover:bg-slate-100 text-fg-muted hover:text-fg font-normal bg-transparent",
        ghostDestructive:
          "hover:bg-de-3 hover:text-de+1 font-normal bg-transparent text-de",
        iconDestructive: "hover:text-de+1 font-normal bg-transparent",
        link: "text-ac+1 underline-offset-4  hover:underline bg-transparent px-0",
        // Transparent wrapper with no hover styles: for controls whose visible
        // content (e.g. a Badge) already draws its own look.
        plain: "bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  alignLeft?: boolean;
  asChild?: boolean;
  isLoading?: boolean;
  spinnerSize?: number;
  fullWidth?: boolean;
  variant?:
    | "default"
    | "destructive"
    | "outlineDestructive"
    | "outline"
    | "secondary"
    | "white"
    | "ghost"
    | "ghostDestructive"
    | "link"
    | "plain"
    | "inputLike"
    | "inputLikeDestructive"
    | "iconDestructive"
    | "selected"
    | "strong";

  size?: "default" | "xs" | "sm" | "md" | "lg" | "xl" | "custom";
  type?: "button" | "submit" | "reset";
  tooltip?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  wrap?: boolean;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { isLoading?: boolean }
>(
  (
    {
      className,
      variant,
      size = "md",
      fullWidth = false,
      asChild = false,
      isLoading = false,
      spinnerSize = 16,
      children,
      alignLeft = false,
      formAction,
      type = "button",
      tooltip,
      iconLeft,
      iconRight,
      wrap = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const shouldWrapDisabledTooltipTrigger = Boolean(
      tooltip && (isLoading || props.disabled),
    );

    const isIconComponent = (child: ReactNode) => {
      return isValidElement(child) && typeof child.type === "function";
    };

    const isIcon = React.Children.toArray(children).some(isIconComponent);

    // Define el contenido interno del botón (iconos, spinner, texto)
    // With asChild, Slot must receive the real child element (not a Fragment),
    // otherwise it clones its props (formAction, className...) onto the Fragment.
    const buttonChildren = asChild ? (
      children
    ) : (
      <>
        {isLoading && <Spinner size={spinnerSize} className="mr-1" />}
        {iconLeft && !isLoading && <span className="mr-1">{iconLeft}</span>}
        {!isLoading ? children : isIcon ? null : children}
        {iconRight}
      </>
    );

    // Define el elemento del botón real (Comp)
    const buttonElement = (
      <Comp
        formAction={formAction}
        className={cn(
          buttonVariants({ variant, size, className }),
          "relative items-center",
          alignLeft ? "justify-start" : "justify-center",
          fullWidth ? "w-full" : "",
          wrap ? "text-wrap leading-tight" : "text-nowrap",
          // Aplica el margen izquierdo solo si está cargando y no es un icono (comportamiento original)
          // isLoading && !isIcon ? "ml-2" : "",
          // Importante: Aplica pointer-events-none solo si hay un tooltip Y el botón está deshabilitado
          tooltip && (isLoading || props.disabled) && "pointer-events-none",
        )}
        ref={ref}
        disabled={isLoading || props.disabled} // El botón sigue estando deshabilitado
        type={type}
        {...props}
      >
        {buttonChildren}
      </Comp>
    );

    return tooltip ? (
      <TooltipProvider>
        <Tooltip>
          {shouldWrapDisabledTooltipTrigger ? (
            <TooltipTrigger asChild>
              <span className={cn("inline-flex", fullWidth && "w-full")}>
                {buttonElement}
              </span>
            </TooltipTrigger>
          ) : (
            <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
          )}
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      // Si no hay tooltip, renderiza el elemento del botón directamente
      buttonElement
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
